import React, { useRef, useEffect, useState, useMemo } from "react";
import { Pose } from "@mediapipe/pose";
import { Camera } from "@mediapipe/camera_utils";
import twemoji from "twemoji";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import { BsPlay, BsPause, BsMusicNoteBeamed, BsGear } from "react-icons/bs";

import Webcam from "react-webcam";
import { drawAnimatedCharacter } from "./characterDrawing.js";

import "./App.css";

import download from "./download.js";
import {
  calculateCalories,
  calculatePixelsPerMeter,
} from "./calorieCalculator.js";
import { smoothKeypoints } from "./poseSmoothing.js";
import SettingsDialog from "./components/SettingsDialog.js";
import CalorieDisplay from "./components/CalorieDisplay.js";

const DEFAULT_HEIGHT_CM = 170;
const DEFAULT_WEIGHT_KG = 70;

const App = () => {
  const [cameraId, setCamera] = React.useState(
    localStorage.getItem("cameraId", "")
  );
  const [isModelLoading, setIsModelLoading] = React.useState(true);
  const [cameraError, setCameraError] = React.useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [calories, setCalories] = useState(0);
  const [audioFile, setAudioFile] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [height, setHeight] = useState(
    parseFloat(localStorage.getItem("height")) || DEFAULT_HEIGHT_CM
  );
  const [weight, setWeight] = useState(
    parseFloat(localStorage.getItem("weight")) || DEFAULT_WEIGHT_KG
  );
  const [faceExpression, setFaceExpression] = useState(
    localStorage.getItem("faceExpression") || "slight"
  );
  const [theme, setTheme] = useState(
    localStorage.getItem("theme") || "system"
  );
  const [backend, setBackend] = useState("Loading...");

  const muiTheme = useMemo(() => {
    let effectiveTheme = theme;
    if (theme === "system") {
      effectiveTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return createTheme({
      palette: {
        mode: effectiveTheme,
      },
    });
  }, [theme]);

  const audioRef = useRef(null);
  const previousPoseRef = useRef(null);
  const lastDrawnPoseRef = useRef(null);
  const smoothedKeypointsRef = useRef(null);
  const smoothedKeypointsForCaloriesRef = useRef(null);
  const emojiImageRef = useRef(null);

  useEffect(() => {
    const savedAudio = localStorage.getItem("audioFile");
    if (savedAudio) {
      setAudioFile(savedAudio);
    }
  }, []);

  useEffect(() => {
    const emojiMap = {
      smile: "1f60a",
      grin: "1f600",
      grinning: "1f603",
      beam: "1f601",
      slight: "1f642",
      none: null
    };
    const emojiCode = emojiMap[faceExpression];

    if (!emojiCode) {
      emojiImageRef.current = null;
      return;
    }

    const img = new Image();
    const emojiUrl = `${twemoji.base}svg/${emojiCode}.svg`;
    img.onload = () => {
      emojiImageRef.current = img;
    };
    img.src = emojiUrl;
  }, [faceExpression]);

  useEffect(() => {
    let cameraInstance = null;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    const initializeMediaPipe = async () => {
      try {
        const pose = new Pose({
          locateFile: (file) => {
            return `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`;
          }
        });

        pose.setOptions({
          modelComplexity: 1,
          smoothLandmarks: true,
          enableSegmentation: false,
          smoothSegmentation: false,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5
        });

        pose.onResults((results) => {
          if (results.poseLandmarks) {
            const convertedPose = convertMediaPipePose(results.poseLandmarks);
            lastDrawnPoseRef.current = convertedPose;

            smoothedKeypointsForCaloriesRef.current = smoothKeypoints(
              convertedPose.keypoints,
              smoothedKeypointsForCaloriesRef.current
            );

            const videoElement = webcamRef.current?.video;
            if (videoElement) {
              const videoWidth = videoElement.videoWidth;
              const videoHeight = videoElement.videoHeight;
              updateSmoothedKeypoints(convertedPose, videoWidth, videoHeight);

              const pixelsPerMeter = calculatePixelsPerMeter(
                height,
                convertedPose.keypoints
              );

              const smoothedPose = {
                ...convertedPose,
                keypoints: smoothedKeypointsForCaloriesRef.current
              };

              const caloriesBurnt = calculateCalories(
                smoothedPose,
                previousPoseRef.current,
                pixelsPerMeter,
                weight
              );
              updateCalories(caloriesBurnt);
              previousPoseRef.current = smoothedPose;
            }
          }
        });

        setBackend(isIOS || isSafari ? "MediaPipe WASM (iOS Optimized)" : "MediaPipe WASM");
        console.log('✅ MediaPipe Pose initialized with WASM backend');

        return pose;
      } catch (error) {
        console.error("Error initializing MediaPipe:", error);
        setBackend("Error");
        throw error;
      }
    };

    initializeMediaPipe()
      .then((pose) => {
        setIsModelLoading(false);
        return navigator.mediaDevices.getUserMedia({ video: true })
          .then(() => {
            requestAnimationFrame(renderCanvas);
            
            if (webcamRef.current?.video) {
              cameraInstance = new Camera(webcamRef.current.video, {
                onFrame: async () => {
                  if (webcamRef.current?.video) {
                    await pose.send({ image: webcamRef.current.video });
                  }
                },
                width: 640,
                height: 480
              });
              cameraInstance.start();
            }
          });
      })
      .catch((error) => {
        console.error("Error initializing:", error);
        setIsModelLoading(false);
        setBackend("Error");
        if (error.name === "NotAllowedError") {
          setCameraError("permission_denied");
        } else if (error.name === "NotFoundError") {
          setCameraError("not_found");
        } else if (error.name === "NotReadableError") {
          setCameraError("not_readable");
        }
      });

    return () => {
      if (cameraInstance) {
        cameraInstance.stop();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cameras = [];

  navigator.mediaDevices.enumerateDevices().then((devices) => {
    devices.forEach(function (device, i) {
      if (device.kind === "videoinput") {
        cameras.push(
          <MenuItem key={i} value={device.deviceId}>
            {device.label}
          </MenuItem>
        );
      }
    });
  });

  const webcamRef = useRef(null);
  const canvasRef = useRef(null);

  const handleChange = (event) => {
    setCamera(event.target.value);
    localStorage.setItem("cameraId", event.target.value);
    console.log({ "camera changed": event.target.value });
  };

  let poses = [];
  // Load posenet

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("audio/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        setAudioFile(dataUrl);
        localStorage.setItem("audioFile", dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const resetCalories = () => {
    setCalories(0);
  };

  const saveSettings = () => {
    localStorage.setItem("height", height.toString());
    localStorage.setItem("weight", weight.toString());
    localStorage.setItem("cameraId", cameraId);
    localStorage.setItem("faceExpression", faceExpression);
    localStorage.setItem("theme", theme);
    setSettingsOpen(false);
  };

  const downloadData = () => {
    // var blob = new Blob([], {
    //   type: "text/plain;charset=utf-8",
    // });
    const file = new File(
      [JSON.stringify(poses)],
      `poses_${new Date().toISOString()}.json`,
      {
        type: "text/plain",
      }
    );
    // download(blob, );
    download(file);
    poses.length = 0;
  };

  const updateCalories = (caloriesBurnt) => {
    if (caloriesBurnt > 0) {
      setCalories((prev) => prev + caloriesBurnt);
    }
  };

  const convertMediaPipePose = (landmarks) => {
    const keypointNames = [
      'nose', 'left_eye_inner', 'left_eye', 'left_eye_outer',
      'right_eye_inner', 'right_eye', 'right_eye_outer',
      'left_ear', 'right_ear', 'mouth_left', 'mouth_right',
      'left_shoulder', 'right_shoulder', 'left_elbow', 'right_elbow',
      'left_wrist', 'right_wrist', 'left_pinky', 'right_pinky',
      'left_index', 'right_index', 'left_thumb', 'right_thumb',
      'left_hip', 'right_hip', 'left_knee', 'right_knee',
      'left_ankle', 'right_ankle', 'left_heel', 'right_heel',
      'left_foot_index', 'right_foot_index'
    ];

    const videoElement = webcamRef.current?.video;
    const videoWidth = videoElement?.videoWidth || window.innerWidth;
    const videoHeight = videoElement?.videoHeight || window.innerHeight;

    const keypoints = landmarks.map((landmark, index) => ({
      name: keypointNames[index],
      x: landmark.x * videoWidth,
      y: landmark.y * videoHeight,
      z: landmark.z,
      score: landmark.visibility
    }));

    return { keypoints };
  };

  const updateSmoothedKeypoints = (pose, videoWidth, videoHeight) => {
    const scaleX = window.innerWidth / videoWidth;
    const scaleY = window.innerHeight / videoHeight;

    const scaledKeypoints = pose["keypoints"].map(kp => ({
      ...kp,
      x: kp.x * scaleX,
      y: kp.y * scaleY
    }));

    smoothedKeypointsRef.current = smoothKeypoints(
      scaledKeypoints,
      smoothedKeypointsRef.current
    );
  };

  const renderCanvas = () => {
    if (!canvasRef.current) {
      requestAnimationFrame(renderCanvas);
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    if (canvas.width !== window.innerWidth || canvas.height !== window.innerHeight) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }

    const bgColor = muiTheme.palette.mode === 'dark' ? '#121212' : '#ffffff';
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (smoothedKeypointsRef.current) {
      drawAnimatedCharacter(smoothedKeypointsRef.current, ctx, emojiImageRef.current);
    }

    requestAnimationFrame(renderCanvas);
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <div className="App">
        <Dialog
          open={cameraError !== null}
          onClose={() => { }}
          aria-labelledby="camera-error-dialog"
        >
          <DialogTitle id="camera-error-dialog">
          {cameraError === "not_found" ? "Camera Not Found" :
            cameraError === "not_readable" ? "Camera Not Available" :
              "Camera Access Required"}
        </DialogTitle>
        <DialogContent>
          {cameraError === "not_found" && (
            <>
              <DialogContentText>
                No camera device was found. Please connect a camera to your device and click the button below to reload.
              </DialogContentText>
              <DialogContentText sx={{ marginTop: "16px", fontStyle: "italic" }}>
                Make sure your camera is properly connected and not being used by another application.
              </DialogContentText>
            </>
          )}
          {cameraError === "not_readable" && (
            <>
              <DialogContentText>
                Camera is not accessible. It may be in use by another application or there may be a hardware issue. Please close other applications using the camera and click the button below to reload.
              </DialogContentText>
              <DialogContentText sx={{ marginTop: "16px", fontStyle: "italic" }}>
                If the problem persists, try restarting your browser or device.
              </DialogContentText>
            </>
          )}
          {cameraError === "permission_denied" && (
            <>
              <DialogContentText>
                This app needs access to your camera to work. Please allow camera permissions in your browser when prompted, or check your browser settings to enable camera access for this site. After granting permission, click the button below to reload.
              </DialogContentText>
              <DialogContentText sx={{ marginTop: "16px", fontStyle: "italic" }}>
                Privacy: This website does not take photos or videos, nor upload any imagery anywhere. Camera access is only used for on-device computations to evaluate your workout performance.
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.location.reload()} color="primary" variant="contained">
            Reload Page
          </Button>
        </DialogActions>
      </Dialog>

      {audioFile && (
        <audio
          ref={audioRef}
          src={audioFile}
          onEnded={() => setIsPlaying(false)}
        />
      )}

      <div
        style={{
          position: "fixed",
          top: "20px",
          left: "20px",
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          alignItems: "flex-start",
        }}
      >
        <CalorieDisplay calories={calories} />

        <input
          type="file"
          accept="audio/*"
          onChange={handleFileUpload}
          style={{ display: "none" }}
          id="audio-upload"
        />
        <label htmlFor="audio-upload">
          <Button
            variant="contained"
            component="span"
            startIcon={<BsMusicNoteBeamed />}
            style={{ background: "#9c27b0" }}
          >
            Upload Song
          </Button>
        </label>

        {audioFile && (
          <div style={{ display: "flex", gap: "10px" }}>
            <Button
              variant="contained"
              onClick={togglePlayPause}
              style={{ background: "#4caf50" }}
              startIcon={isPlaying ? <BsPause /> : <BsPlay />}
            >
              {isPlaying ? "Pause" : "Play"}
            </Button>
            <Button
              variant="outlined"
              onClick={resetCalories}
              style={{ color: "white", borderColor: "white" }}
            >
              Reset
            </Button>
          </div>
        )}

        <Button
          variant="outlined"
          onClick={downloadData}
          size="small"
          style={{ color: "white", borderColor: "white" }}
        >
          Download Data
        </Button>
      </div>

      <IconButton
        onClick={() => setSettingsOpen(true)}
        style={{
          position: "fixed",
          right: "20px",
          top: "20px",
          zIndex: 100,
          background: "rgba(0,0,0,0.6)",
          color: "white",
        }}
      >
        <BsGear size={24} />
      </IconButton>

      <div
        style={{
          position: "fixed",
          right: "70px",
          top: "20px",
          zIndex: 100,
          background: "rgba(0,0,0,0.7)",
          color: "white",
          padding: "8px 12px",
          borderRadius: "8px",
          fontSize: "12px",
          fontFamily: "monospace",
        }}
      >
        {backend}
      </div>

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        height={height}
        setHeight={setHeight}
        weight={weight}
        setWeight={setWeight}
        cameraId={cameraId}
        handleCameraChange={handleChange}
        cameras={cameras}
        faceExpression={faceExpression}
        setFaceExpression={setFaceExpression}
        theme={theme}
        setTheme={setTheme}
        onSave={saveSettings}
      />

      <header className="App-header">
        <Webcam
          ref={webcamRef}
          videoConstraints={{
            deviceId: cameraId,
            width: window.innerWidth,
            height: window.innerHeight
          }}
          style={{
            visibility: "hidden",
            position: "fixed",
            top: 0,
            left: 0,
            width: "1px",
            height: "1px",
            zIndex: -1,
          }}
        ></Webcam>
        {isModelLoading && (
          <div style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            textAlign: "center"
          }}>
            <img src="/logo.svg" className="App-logo" alt="logo" style={{ height: "20vmin" }} />
            <div style={{ color: "white", marginTop: "20px", fontSize: "18px" }}>
              Loading models...
            </div>
          </div>
        )}
        <canvas
          ref={canvasRef}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 2,
            pointerEvents: "none",
            transform: "scaleX(-1)",
          }}
        ></canvas>
      </header>
    </div>
    </ThemeProvider>
  );
};

export default App;
